<div class="trust-verification-report">
  <PageControl
    :title="'Trust Search Verification Report'"
    :elements_query="sheet_elements"
  >
    <button
      class="btn btn-bg-green text-xs rounded-small shadow-sm me-2"
      @click="show_export_modal"
    >
      <i class="fas fa-download me-1"></i> Export Data
    </button>
  </PageControl>

  <!-- Export Modal - Teleported to body to prevent z-index issues -->
  <Teleport to="body">
    <div
      class="modal fade"
      :id="'export-modal-' + report_reference"
      tabindex="-1"
      aria-labelledby="exportModalLabel"
      aria-hidden="true"
    >
      <div class="modal-dialog modal-dialog-centered">
        <div class="modal-content rounded-4 shadow">
          <div class="modal-header">
            <h6 class="modal-title fw-semibold text-muted">
              Export Trust Search Verification Report Data
            </h6>
          </div>
          <div class="modal-body">
            <div class="mb-3">
              <label class="form-label fw-bold">Select Data to Export:</label>
              <div class="form-check">
                <input
                  class="form-check-input me-2"
                  type="checkbox"
                  id="export-trust-info"
                  v-model="export_options.trust_information"
                />
                <label class="form-check-label" for="export-trust-info">
                  <strong>Trust Information</strong>
                  <small class="d-block text-muted"
                    >Trust details, file number, masters office, and authorization information</small
                  >
                </label>
              </div>
              <div class="form-check">
                <input
                  class="form-check-input me-2"
                  type="checkbox"
                  id="export-trustees-details"
                  v-model="export_options.trustees_details"
                />
                <label class="form-check-label" for="export-trustees-details">
                  <strong>Trustees Details</strong>
                  <small class="d-block text-muted"
                    >Individual trustee information and demographics</small
                  >
                </label>
              </div>
              <div class="form-check">
                <input
                  class="form-check-input me-2"
                  type="checkbox"
                  id="export-transaction-details"
                  v-model="export_options.transaction_details"
                />
                <label class="form-check-label" for="export-transaction-details">
                  <strong>Transaction Details</strong>
                  <small class="d-block text-muted"
                    >Enquiry information, reference numbers, and processing details</small
                  >
                </label>
              </div>
              <div class="form-check">
                <input
                  class="form-check-input me-2"
                  type="checkbox"
                  id="export-raw-json"
                  v-model="export_options.raw_json"
                />
                <label class="form-check-label" for="export-raw-json">
                  <strong>Raw JSON Data</strong>
                  <small class="d-block text-muted"
                    >Original unprocessed data from the trust search</small
                  >
                </label>
              </div>
            </div>
            <div class="mb-3">
              <label class="form-label fw-bold">Export Format:</label>
              <div class="form-check">
                <input
                  class="form-check-input"
                  type="radio"
                  name="exportFormat"
                  id="export-format-csv"
                  value="csv"
                  v-model="export_format"
                />
                <label class="form-check-label" for="export-format-csv">
                  CSV (Separate files for each table)
                </label>
              </div>
              <div class="form-check">
                <input
                  class="form-check-input"
                  type="radio"
                  name="exportFormat"
                  id="export-format-excel"
                  value="excel"
                  v-model="export_format"
                />
                <label class="form-check-label" for="export-format-excel">
                  Excel (Multiple sheets in one file)
                </label>
              </div>
              <div class="form-check">
                <input
                  class="form-check-input"
                  type="radio"
                  name="exportFormat"
                  id="export-format-json"
                  value="json"
                  v-model="export_format"
                />
                <label class="form-check-label" for="export-format-json">
                  JSON (JavaScript Object Notation)
                </label>
              </div>
            </div>
          </div>
          <div class="modal-footer">
            <button
              type="button"
              class="btn btn-bg-secondary text-xs rounded-small shadow-sm me-2"
              @click="close_modal"
            >
              Cancel
            </button>
            <button
              type="button"
              class="btn btn-bg-green text-xs rounded-small shadow-sm"
              @click="export_data"
              :disabled="!has_selected_tables"
            >
              <i class="fas fa-download me-1"></i>
              Export Data
            </button>
          </div>
        </div>
      </div>
    </div>
  </Teleport>

  <PageComponent>
    <SectionSheet>
      <div
        class="container-fluid py-2 header d-flex align-items-center justify-content-between border-bottom mb-2"
      >
        <div class="d-flex">
          <img
            class="logo"
            width="295"
            height="125"
            src="@/assets/images/new_black_logo2.svg"
            alt="Logo"
          />
        </div>
        <div class="d-flex text-end">
          <div>
            <div class="h5 fw-bold mb-1">Trust Search Verification Report</div>
            <div class="text-muted text-xs">
              28 Fricker Rd,<br />
              Illovo, Johannesburg,<br />
              2196 South Africa<br />
              Website:
              <a
                href="https://www.goodpeopledata.com/"
                target="_blank"
                class="text-decoration-none text-muted"
                >www.goodpeopledata.com</a
              >
            </div>
          </div>
        </div>
      </div>

      <div class="card shadow-sm p-2 bg-white mb-2">
        <div class="card-header">
          <h6 class="mb-0">
            <i class="fas fa-shield-alt me-2"></i>
            Trust Search Verification Summary
          </h6>
        </div>
        <div class="card-body">
          <p>
            This report summarizes the trust search verification for
            <span class="fw-bold text-dark">{{ trust_name }}</span> (File Number:
            <span class="fw-bold text-dark">{{ file_number }}</span>), registered with the
            <span class="fw-bold text-dark">{{ masters_office }}</span>
            (Reference ID: <span class="fw-bold text-dark">{{ client_user_reference }}</span>).
          </p>
        </div>
      </div>

      <!-- Report Header -->
      <div class="row mb-4">
        <div class="col-12">
          <div class="card shadow-sm">
            <div class="card-header">
              <h6 class="mb-0">
                <i class="fas fa-shield-alt me-2"></i>
                Trust Search Verification Report
              </h6>
              <div class="text-muted small">
                Reference: {{ client_user_reference }} | Generated: {{ formatted_current_date }}
              </div>
            </div>
            <div class="card-body">
              <div class="row">
                <div class="col-md-6">
                  <div class="mb-2">
                    <span class="label-modern">Trust Name:</span>
                    <div class="fw-semibold">{{ trust_name }}</div>
                  </div>
                  <div class="mb-2">
                    <span class="label-modern">File Number:</span>
                    <div class="fw-semibold">{{ file_number }}</div>
                  </div>
                  <div class="mb-2">
                    <span class="label-modern">Masters Office:</span>
                    <div class="fw-semibold">{{ masters_office }}</div>
                  </div>
                </div>
                <div class="col-md-6">
                  <div class="mb-2">
                    <span class="label-modern mb-2">Verification Status:</span>
                    <div>
                      <span :class="verification_status_badge_class" class="status-badge">
                        <i :class="verification_status_icon" class="me-1"></i>
                        {{ overall_verification_status }}
                      </span>
                    </div>
                  </div>
                  <div class="mb-2">
                    <span class="label-modern">Authorization Date:</span>
                    <div class="fw-semibold">{{ formatted_authorization_date }}</div>
                  </div>
                  <div class="mb-2">
                    <span class="label-modern">Total Trustees:</span>
                    <div class="fw-semibold">{{ total_trustees }}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Summary Section -->
      <div class="row mb-4">
        <div class="col-12">
          <div class="card shadow-sm">
            <div class="card-header">
              <h6 class="mb-0">
                <i class="fas fa-clipboard-check me-2"></i>
                Search Summary
              </h6>
            </div>
            <div class="card-body">
              <div class="row">
                <div class="col-md-6">
                  <div class="mb-2">
                    <span class="label-modern">Trust Found:</span>
                    <div class="fw-semibold">{{ trust_found_status }}</div>
                  </div>
                  <div class="mb-2">
                    <span class="label-modern">Active Trustees:</span>
                    <div class="fw-semibold">{{ active_trustees_count }}</div>
                  </div>
                </div>
                <div class="col-md-6">
                  <div class="mb-2">
                    <span class="label-modern">Verified Trustees:</span>
                    <div class="fw-semibold">{{ verified_trustees_count }}</div>
                  </div>
                  <div class="mb-2">
                    <span class="label-modern">Client Reference:</span>
                    <div class="fw-semibold">{{ client_user_reference }}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Report Footer -->
      <div class="report-meta">
        <div class="text-muted small">
          Report Reference: {{ client_user_reference }}
        </div>
        <div class="text-muted small">
          Generated: {{ formatted_current_date }}
        </div>
      </div>
    </SectionSheet>
    
    <SectionSheet>
      <!-- Trustees Information Section -->
      <div class="row mb-4">
        <div class="col-12">
          <div class="card shadow-sm">
            <div class="card-header">
              <h6 class="mb-0">
                <i class="fas fa-users me-2"></i>
                Trustees Information
              </h6>
            </div>
            <div class="card-body">
              <div class="table-responsive">
                <table class="table table-bordered table-hover table-compact">
                  <thead class="table-light">
                    <tr>
                      <th class="text-xs">#</th>
                      <th class="text-xs">Full Names</th>
                      <th class="text-xs">ID Number</th>
                      <th class="text-xs">Date of Birth</th>
                      <th class="text-xs">Age</th>
                      <th class="text-xs">Gender</th>
                      <th class="text-xs">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(trustee, index) in trustees_list" :key="index">
                      <td class="text-xs fw-semibold">{{ index + 1 }}</td>
                      <td class="text-xs fw-semibold">{{ trustee.FullNames }}</td>
                      <td class="text-xs">{{ trustee.IDNumber || 'Not Available' }}</td>
                      <td class="text-xs">{{ formatTrusteeDate(trustee.DateOfBirth) }}</td>
                      <td class="text-xs">{{ trustee.Age === '0' ? 'Not Available' : trustee.Age }}</td>
                      <td class="text-xs">{{ formatGender(trustee.Gender) }}</td>
                      <td class="text-xs">
                        <span
                          :class="getTrusteeStatusBadgeClass(trustee)"
                          class="badge"
                        >
                          <i
                            :class="getTrusteeStatusIcon(trustee)"
                            class="me-1"
                          ></i>
                          {{ getTrusteeStatusText(trustee) }}
                        </span>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Report Footer -->
      <div class="report-meta">
        <div class="text-muted small">
          Report Reference: {{ client_user_reference }}
        </div>
        <div class="text-muted small">
          Generated: {{ formatted_current_date }}
        </div>
      </div>
    </SectionSheet>

    <SectionSheet>
      <!-- Trust Details Section -->
      <div class="row mb-4">
        <div class="col-12">
          <div class="card shadow-sm">
            <div class="card-header">
              <h6 class="mb-0">
                <i class="fas fa-info-circle me-2"></i>
                Additional Trust Details
              </h6>
            </div>
            <div class="card-body">
              <div class="table-responsive mt-2">
                <table class="table table-bordered table-hover table-compact">
                  <thead class="table-light">
                    <tr>
                      <th class="text-xs">Field</th>
                      <th class="text-xs">Value</th>
                      <th class="text-xs">Description</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td class="text-xs fw-semibold">Trust Name</td>
                      <td class="text-xs">{{ trust_name }}</td>
                      <td class="text-xs">Full legal name of the trust</td>
                    </tr>
                    <tr>
                      <td class="text-xs fw-semibold">File Number</td>
                      <td class="text-xs">{{ file_number }}</td>
                      <td class="text-xs">Official masters office file number</td>
                    </tr>
                    <tr>
                      <td class="text-xs fw-semibold">Masters Office</td>
                      <td class="text-xs">{{ masters_office }}</td>
                      <td class="text-xs">Registering masters office location</td>
                    </tr>
                    <tr>
                      <td class="text-xs fw-semibold">Authorization Date</td>
                      <td class="text-xs">{{ formatted_authorization_date }}</td>
                      <td class="text-xs">Date when trust was authorized</td>
                    </tr>
                    <tr>
                      <td class="text-xs fw-semibold">Total Trustees</td>
                      <td class="text-xs">{{ total_trustees }}</td>
                      <td class="text-xs">Total number of appointed trustees</td>
                    </tr>
                    <tr>
                      <td class="text-xs fw-semibold">Verified Trustees</td>
                      <td class="text-xs">{{ verified_trustees_count }}</td>
                      <td class="text-xs">Number of trustees with complete information</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Report Footer -->
      <div class="report-meta">
        <div class="text-muted small">
          Report Reference: {{ client_user_reference }}
        </div>
        <div class="text-muted small">
          Generated: {{ formatted_current_date }}
        </div>
      </div>
    </SectionSheet>

    <SectionSheet>
      <div class="card shadow-sm">
        <div class="card-header">
          <h6 class="mb-0">
            <i class="fas fa-exclamation-triangle me-2"></i>
            Disclaimer
          </h6>
        </div>
        <div class="card-body">
          <div class="disclaimer-content">
            <h6 class="mb-3 border-bottom pb-2">
              <i class="bi bi-info-circle me-2"></i>General Information
            </h6>
            <p class="text-xs mb-4">
              This Trust Search Verification Report is prepared solely for the benefit
              of the intended recipient and is based on data obtained from authorized
              masters offices. While <strong> THE GOOD PEOPLE DATA COMPANY (PTY) LTD ('TGPDC')
                </strong> employs industry-standard practices to collect and process information,
              TGPDC does not guarantee, and expressly disclaims, the accuracy, completeness
              or timeliness of any data contained herein. The Report reflects the
              status of each record at the moment of generation and may not
              reflect subsequent changes.
            </p>
            <h6 class="mb-3 border-bottom pb-2">
              <i class="bi bi-shield-lock me-2"></i>Data Privacy
            </h6>
            <p class="text-xs mb-4">
              All trust and trustee information is processed in accordance with
              applicable data protection laws including POPIA (Protection of
              Personal Information Act). This report should only be shared with
              authorized personnel who require access to this information for
              legitimate business purposes.
            </p>
            <div class="text-center text-muted mt-3">
              <p class="mb-0 text-xs">
                © {{ new Date().getFullYear() }} The Good People Data Company.
                All rights reserved.
              </p>
            </div>
          </div>
        </div>
      </div>

      <!-- Report Footer -->
      <div class="report-meta">
        <div class="text-muted small">
          Report Reference: {{ client_user_reference }}
        </div>
        <div class="text-muted small">
          Generated: {{ formatted_current_date }}
        </div>
      </div>
    </SectionSheet>
  </PageComponent>
</div>